import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebouncedCallback } from "use-debounce";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerTitle,
	DrawerTrigger,
} from "../components/ui/dropdownMobile";
import type {
	EventParameters,
	WorkshopData,
	WorkshopListEndpoint,
} from "../api/api";
// eslint-disable-next-line no-duplicate-imports
import { getEventList } from "../api/api";
import type { ParsedLocation } from "@tanstack/react-router";
// eslint-disable-next-line no-duplicate-imports
import {
	Link,
	useLoaderData,
	useLocation,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { MagnifyingGlassIcon } from "../components/icons/magnifyingGlass";
import { ArrowIcon } from "../components/icons/arrow";
import type { AxiosError } from "axios";
import CoverCardMVP from "../components/shared/CoverCardMVP";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import TextFragment from "../components/ui/textFragment";
import { useReducer, useRef, useCallback, useEffect } from "react";
import { LoaderSpinner } from "../components/ui/LoaderSpinner";
import type { StudyStatus } from "@/routes/(app)/_app.study.$studyId";
import { useAuth } from "@/context/AuthContext";
import CoverCardNewVisual from "../components/shared/CoverCardNewVisual";
import { FEATURE_FLAG_COVER_CARD_NEW_VISUAL } from "./Home";
import { EndOfTheListMessage } from "../components/ui/EndOfTheListMessage";

const getEventCategoryFromPath = (pathanme: string): "training" | "webinar" =>
	pathanme.includes("training") ? "training" : "webinar";
const PER_PAGE = 7;

// Definicje typów dla reducera UI
interface UIState {
	isDrawerOpen: boolean;
	searchInputValue: string;
}

type UIAction =
	| { type: "TOGGLE_DRAWER"; payload: boolean }
	| { type: "SET_SEARCH_INPUT"; payload: string };

// Reducer do zarządzania stanem UI
function uiReducer(state: UIState, action: UIAction): UIState {
	switch (action.type) {
		case "TOGGLE_DRAWER":
			return { ...state, isDrawerOpen: action.payload };
		case "SET_SEARCH_INPUT":
			return { ...state, searchInputValue: action.payload };
		default:
			return state;
	}
}

interface RedirectionAuthParameters {
	params: {
		slugId: string;
	};
	search: {
		status: StudyStatus;
	};
	to: string;
}

interface RedirectionNoAuthParameters {
	params: {
		workshopId: string;
	};
	search: {
		status: StudyStatus;
	};
	to: string;
}

const redirectionByAuthStatus = (
	workshop: WorkshopData,
	location: ParsedLocation<{
		search?: string | undefined;
		status?: StudyStatus | undefined;
		category?: string | undefined;
	}>,
	isAuthenticated: boolean
): RedirectionAuthParameters | RedirectionNoAuthParameters => {
	if (isAuthenticated) {
		return {
			params: { slugId: workshop.slug },
			search: { status: workshop.type_field as StudyStatus },
			to: `${location.pathname}/$slugId/auth`,
		};
	}
	return {
		params: { workshopId: workshop.id.toString() },
		search: { status: workshop.type_field as StudyStatus },
		to: `${location.pathname}/$workshopId`,
	};
};

export const WorkshopsGlobalListMVP = (): JSX.Element => {
	const parameters = useSearch({ from: "/(app)/_app/workshops/$type" });
	const navigate = useNavigate();
	const location = useLocation();
	const { categories } = useLoaderData({ from: "/(app)/_app/workshops/$type" });
	const { isAuthenticated } = useAuth();

	// Stan UI zarządzany przez reducer
	const [uiState, uiDispatch] = useReducer(uiReducer, {
		isDrawerOpen: false,
		searchInputValue: parameters.search || "",
	});

	// Wartości pobierane bezpośrednio z URL
	const currentViewStatus = parameters.viewStatus || "UPCOMING";
	const currentCategory =
		typeof parameters.category !== "undefined" && categories
			? categories.find((cat) => cat.id === Number(parameters.category))
					?.nazwa || "Wszystkie"
			: "Wszystkie";

	// Aktualizacja lokalnego stanu search kiedy zmienia się parametr URL
	useEffect(() => {
		if (parameters.search !== undefined) {
			uiDispatch({
				type: "SET_SEARCH_INPUT",
				payload: parameters.search || "",
			});
		}
	}, [parameters.search]);

	const {
		isError,
		isPending,
		isLoading,
		isFetching,
		isRefetching,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		error,
		data,
	} = useInfiniteQuery<WorkshopListEndpoint, AxiosError>({
		queryKey: ["workshopsList", parameters, currentViewStatus],
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const result = await getEventList(
				parameters as EventParameters,
				getEventCategoryFromPath(location.pathname),
				currentViewStatus,
				Number(pageParam),
				PER_PAGE
			);
			if (!result) throw new Error("Failed to fetch events");
			return result;
		},
		getNextPageParam: (lastPage) => {
			const { page, total } = lastPage;
			const totalPages = Math.ceil(total / PER_PAGE);
			return page < totalPages ? Number(page) + 1 : undefined;
		},
	});

	const debouncedSearch = useDebouncedCallback(async (value: string) => {
		await navigate({
			to: location.pathname,
			search: {
				search: value,
				//   page: 1,
				viewStatus: parameters.viewStatus,
				category: parameters.category,
			},
			replace: true,
		});
	}, 1000);

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLInputElement>
	): void => {
		if (event.key === "Enter") {
			event.preventDefault();
			(event.target as HTMLInputElement).blur();
		}
	};

	const handleSearchChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	): Promise<void> => {
		// Natychmiastowa aktualizacja lokalnego stanu
		uiDispatch({
			type: "SET_SEARCH_INPUT",
			payload: event.target.value,
		});
		// Debounced aktualizacja URL
		await debouncedSearch(event.target.value);
	};

	const loadMore = async (): Promise<void> => {
		if (hasNextPage) {
			await fetchNextPage();
		}
	};

	const observer = useRef<IntersectionObserver | null>(null);
	const lastItemRef = useCallback(
		(node: Element | null) => {
			if (node === null || isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (
					entries &&
					entries.length > 0 &&
					entries[0]?.isIntersecting &&
					hasNextPage
				) {
					void loadMore();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	const items = data?.pages.flatMap((page) => page.items) ?? [];

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			<div className="w-full flex border-placeholder border-2 rounded-lg bg-placeholder px-3 py-4">
				<MagnifyingGlassIcon />
				<input
					className="w-full bg-muted pl-3 hover:border-none focus:outline-none placeholder:text-slate-500"
					value={uiState.searchInputValue}
					placeholder="Szukaj"
					type="search"
					onKeyDown={handleKeyDown}
					onChange={handleSearchChange}
				/>
			</div>

			<div className="flex flex-nowrap"></div>

			<Drawer
				open={uiState.isDrawerOpen}
				onOpenChange={(open) => {
					uiDispatch({ type: "TOGGLE_DRAWER", payload: open });
				}}
			>
				<DrawerTrigger className="relative w-full border-2 border-dropdownBorder rounded-lg my-4 mt-5 group">
					<TextFragment
						as="div"
						className="absolute top-[-9px] bg-mainBackground ml-3 px-2 text-slate-500"
						variant="link"
					>
						Kategorie
					</TextFragment>
					<div className="flex items-center justify-between p-3">
						<TextFragment as="div" className="text-slate-500" variant="text2">
							{currentCategory}
						</TextFragment>
						<span className="-rotate-90 transition-transform group-focus:rotate-90">
							<ArrowIcon />
						</span>
					</div>
				</DrawerTrigger>
				<DrawerTitle />
				<DrawerDescription className="sr-only">
					Lista dostępnych kategorii
				</DrawerDescription>
				<DrawerContent>
					<div className="fixed inset-x-2 bottom-[80px] z-50 bg-white rounded-xl">
						<TextFragment
							as="h3"
							className="py-3 text-center text-text3"
							variant="tag"
						>
							Kategoria
						</TextFragment>
						<ul className="text-center max-h-[60vh] overflow-y-scroll">
							<TextFragment
								key="00-wszystkie"
								as="li"
								className="py-3 m-1 border-t border-dropdownBorder"
								variant="list"
								onClick={async () => {
									uiDispatch({ type: "TOGGLE_DRAWER", payload: false });
									await navigate({
										to: location.pathname,
										search: {
											category: undefined,
											search: "",
											viewStatus: parameters.viewStatus,
										},
										replace: true,
									});
								}}
							>
								Wszystkie
							</TextFragment>
							{categories &&
								categories.map((category) => (
									<TextFragment
										key={category.id}
										as="li"
										className="py-3 m-1 border-t border-dropdownBorder"
										variant="list"
										onClick={async () => {
											uiDispatch({ type: "TOGGLE_DRAWER", payload: false });
											await navigate({
												to: location.pathname,
												search: {
													category: String(category.id),
													search: "",
													viewStatus: parameters.viewStatus,
												},
												replace: true,
											});
										}}
									>
										{category.nazwa}
									</TextFragment>
								))}
						</ul>
					</div>
				</DrawerContent>
			</Drawer>

			<Tabs className="w-full pb-4" defaultValue={currentViewStatus}>
				<TabsList className="w-full">
					<TabsTrigger
						className="w-1/2 p-2"
						value="UPCOMING"
						onClick={async () => {
							await navigate({
								to: location.pathname,
								search: {
									viewStatus: "UPCOMING",
									category: parameters.category,
									search: parameters.search,
								},
								replace: true,
							});
						}}
					>
						Nadchodzące
					</TabsTrigger>
					<TabsTrigger
						className="w-1/2 p-2"
						value="RECORDED"
						onClick={async () => {
							await navigate({
								to: location.pathname,
								search: {
									category: parameters.category,
									search: parameters.search,
									viewStatus: "RECORDED",
								},
								replace: true,
							});
						}}
					>
						Nagrania
					</TabsTrigger>
				</TabsList>
			</Tabs>

			<div className="flex flex-col gap-3">
				{(isPending || isLoading || isFetching) && (
					<>
						<Skeleton className="h-[175px] w-full mb-2 rounded-xl" />
						<Skeleton className="h-[175px] w-full mb-2 rounded-xl" />
					</>
				)}
				{!isRefetching &&
					items.map((post, index) => (
						<Link
							key={`CoverCardMVP-workshops--${post.id}`}
							{...redirectionByAuthStatus(post, location, isAuthenticated)}
						>
							{FEATURE_FLAG_COVER_CARD_NEW_VISUAL ? (
								<div ref={items?.length - 1 === index ? lastItemRef : null}>
									<CoverCardNewVisual
										// ref={items?.length - 1 === index ? lastItemRef : null}
										authors={post.authors_users_data}
										categories={post.kategoria}
										coverId={post.id}
										title={post.nazwa}
										coverDate={
											currentViewStatus === "RECORDED"
												? Number(post["legal-status"])
												: Number(post.starts_at)
										}
										slug={post.slug}
									/>
								</div>
							) : (
								<div ref={items?.length - 1 === index ? lastItemRef : null}>
									<CoverCardMVP
										// ref={items?.length - 1 === index ? lastItemRef : null}
										authors={post.authors_users_data}
										categories={post.kategoria}
										coverId={post.id}
										title={post.nazwa}
										coverDate={
											currentViewStatus === "RECORDED"
												? Number(post["legal-status"])
												: Number(post.starts_at)
										}
										slug={post.slug}
									/>
								</div>
							)}
						</Link>
					))}
			</div>
			{isFetchingNextPage && <LoaderSpinner className="mt-6" size={6} />}
			{!hasNextPage && !isFetching && (
				<EndOfTheListMessage>
					{items.length > 0
						? "Koniec listy szkoleń"
						: "Brak szkoleń w podanych kryteriach"}
				</EndOfTheListMessage>
			)}
			{isError && <EndOfTheListMessage>{error.message}</EndOfTheListMessage>}
		</main>
	);
};
